import { useState, useRef, useLayoutEffect, useCallback } from 'react'
import { List } from 'react-virtualized'

export interface UseRowHeightsCacheProps {
  maxCellHeightsByRow: {
    readonly [key: string]: number
  }
  updateCellHeight: (rowDataID: string, height: number) => void
  registerListRef: (ref: any) => void
  recomputeRowHeights: () => void
}

type UseRowHeightsCache = () => UseRowHeightsCacheProps

interface ReactVirtualizedListRef {
  recomputeRowHeights: List['recomputeRowHeights']
}

// This hook is for use with the `react-virtualized` `List` component
// This stores a cache of row heights by data row ID and calls
// `List#recomputeRowHeights` at the appropriate time
// https://github.com/bvaughn/react-virtualized/blob/master/docs/List.md#recomputerowheights-index-number
export const useRowHeightsCache: UseRowHeightsCache = () => {
  const [maxCellHeightsByRow, setMaxCellHeightsByRow] = useState({})

  const updateCellHeight = (rowDataId: string, height: number) => {
    setMaxCellHeightsByRow({
      ...maxCellHeightsByRow,
      [rowDataId]: height
    })
  }

  const ref = useRef<ReactVirtualizedListRef | null>(null)

  const recomputeRowHeights = useCallback(() => {
    if (ref.current && ref.current.recomputeRowHeights) {
      ref.current.recomputeRowHeights()
    }
  }, [])

  useLayoutEffect(() => {
    recomputeRowHeights()
  }, [maxCellHeightsByRow, recomputeRowHeights])

  const registerListRef = (newRef: ReactVirtualizedListRef) => {
    ref.current = newRef
  }

  return {
    maxCellHeightsByRow,
    updateCellHeight,
    registerListRef,
    recomputeRowHeights
  }
}
